import { Component, DestroyRef, forwardRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PrAutresFormBuilder } from './ea-autres-form.builder';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { AssetDao } from '@app/dao/asset/asset.dao';
import { UserDao } from '@app/dao/user/user.dao';
import * as _ from 'lodash';
import {
  Subject,
  BehaviorSubject,
  combineLatest,
  map,
  of,
  switchMap,
} from 'rxjs';

@Component({
  selector: 'ea-autres-form',
  templateUrl: './ea-autres-form.component.html',
  styleUrls: ['./ea-autres-form.component.scss'],
  providers: [
    {
      provide: PrFormAbstract,
      useExisting: forwardRef(() => EaAutresFormComponent),
    },
  ],
  standalone: false,
})
export class EaAutresFormComponent
  extends PrAutresFormBuilder
  implements OnInit
{
  users: any[];
  references: any;
  groupes: any[];
  sources: any[];
  managers: any[];
  _ = _;
  doneSubject: Subject<void> = new Subject<void>();
  tabChanged: BehaviorSubject<any> = new BehaviorSubject(null);
  changeTab: BehaviorSubject<any> = new BehaviorSubject(null);

  // Étape de choix affichée à l'ajout (sans type imposé) : produits de
  // diversification. Les fonds FPCI/FPCR/FCPR/FPS arrivent toujours AVEC un
  // type (via le chooser du formulaire PME) et sautent donc cette étape.
  // NB: liste figée volontairement — injecter AssetTypeService créerait un
  // cycle d'import (asset-type.service importe EaAutresFormComponent).
  // Garder alignée sur AssetTypeService.getOthersAssetsTypeOptions().
  products = [
    {
      id: 'DIVERSIFICATION_INVESTMENT',
      label: 'Investissement de diversification',
    },
    { id: 'TITRES_PARTICIPATIFS', label: 'Titres participatifs' },
    { id: 'OBLIGATIONS', label: 'Obligations' },
  ];

  constructor(
    protected formConstructor: PrFormConstructor,
    private userDao: UserDao,
    private assetDao: AssetDao,
    private destroyRef: DestroyRef,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    combineLatest([
      this.userDao.getCouple(),
      this.assetDao.getReferences(),
      // À l'ajout via l'étape de choix, le type n'est pas encore défini : on
      // charge les gestionnaires après sélection (selectType).
      this.entity.type
        ? this.assetDao.getProviderByType(this.entity.type.toUpperCase())
        : of([]),
    ]).subscribe(([users, references, managers]) => {
      this.users = users;
      this.references = references;
      this.sources = references[0];
      this.managers = managers;
      this.setDefaultValue();
    });
  }

  setDefaultValue() {
    if (this.entity.owners && this.entity.owners.length)
      this.form.get('owners').setValue(this.buildInitialOwners(this.entity));

    if (this.entity.managerProviderId && !this.form.get('contratId').value) {
      const provider = this.managers?.find(
        m => m.id === this.entity.managerProviderId,
      );
      if (provider) this.form.get('contratId').setValue(provider);
    }
    if (this.entity.subscriptionSource && !this.form.get('source').value) {
      this.form.get('source').setValue(this.entity.subscriptionSource);
    }
  }

  get title() {
    return this.generateTitle("Modifier l'actif", 'Ajout');
  }

  generateTitle(editTitle: string, addTitle: string) {
    if (!this.entity || !this.entity.id) return addTitle;
    if (!this.entity.label || this.entity.label === '') return editTitle;
    return `${editTitle} “${this.entity.label}”`;
  }

  ///REFACTO
  selectType(type) {
    this.form.controls.type.setValue(type);
    this.form.updateValueAndValidity();
    this.assetDao
      .getProviderByType(type)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        this.managers = res;
      });
  }

  onSubmit() {
    this.displayErrors();

    this.selectErrorInput();

    if (this.form.invalid) {
      this.changeTab.next('FIRST');
      return;
    }

    //Fetch every form controls value
    let formDTOExtraction = this.extract();
    this.prFormService.loading();
    this.assetDao
      .proposeManagerProvider(formDTOExtraction, this.managers)
      .pipe(
        map((proposed: any) => {
          if (!formDTOExtraction.managerProviderId) {
            const id = proposed?.id ?? proposed?.data?.id;
            if (id) formDTOExtraction.managerProviderId = id;
          }
          return formDTOExtraction;
        }),
        switchMap(dto => this.assetDao.saveAssets(dto)),
      )
      .subscribe(
        success => {
          this.prFormService.loaded();
          this.doneSubject.next();
          this.notificationService.success('La sauvegarde a réussi');
        },
        errors => {
          console.error(errors);
          this.prFormService.loaded();
          this.notificationService.error('La sauvegarde a échoué');
        },
      );
  }

  onCancel() {
    this.prFormService.close();
  }

  onTabChange(isLastTab: boolean) {
    this.tabChanged.next(isLastTab);
  }

  nextTab() {
    this.changeTab.next('NEXT');
  }
}
